/* ----------------------------------------------------------------------
   src/components/Alarme/Monitoramento/monitoramento.js
   Versão corrigida - remove variáveis não definidas
---------------------------------------------------------------------- */
import React, { useState, useEffect } from "react";
import "../../../styles/desktop/monitoramento.css";
import planta from "../../../assets/img/centralDeAlarme/alarmeDeIncendio.png";
import { PosicaoDeDispositivosAlarmeDeIncendio as devicePositions } from "../../../configs/centralDevicesConfig";

/**
 * Componente principal de monitoramento da CIE
 * – Recebe dados e funções do hook useMonitoramento via props
 * – Exibe mapa interativo e lista de eventos
 * – Mostra dashboard de contadores
 * – Renderiza marcadores verdes (normal) e pulsantes (fault/fire)
 */
export default function Monitoramento({ monitoramentoUtils }) {
  const {
    cieData,
    loading,
    fetchCieData,
    formatDateTime,
    getActiveAlerts,
    getDeviceSeverity
  } = monitoramentoUtils;

  /* ──────────────────────────── State Local (UI) ──────────────────────────── */
  const [viewMode, setViewMode] = useState("mapa");          // "mapa" | "lista"
  const [showDashboard, setShowDashboard] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [severityFilter, setSeverityFilter] = useState("all"); // "all" | "fire" | "fault" | "info"

  /* ───────────────────── Render markers (normais) ───────────────────── */
  const renderDeviceMarkers = () =>
    Object.entries(devicePositions).map(([deviceKey, pos]) => {
      const sev = getDeviceSeverity(deviceKey);
      if (sev !== "normal") return null; // será coberto por alerta
      return (
        <div
          key={`device-${deviceKey}`}
          className={`device-marker ${sev}`}
          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
          title={deviceKey}
        >
          <div className="device-dot" />
        </div>
      );
    });

  /* ───────────────────── Render markers (alertas) ───────────────────── */
  const renderMapAlerts = () =>
    getActiveAlerts().map((event, idx) => {
      const pos = devicePositions[event.deviceKey];
      if (!pos) {
        console.warn(`⚠️ Sem coordenadas para ${event.deviceKey}`);
        return null;
      }
      return (
        <div
          key={`alert-${idx}`}
          className={`alert-marker ${event.severity.toLowerCase()}`}
          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
          onClick={() => setSelectedEvent(event)}
          title={`${event.deviceName} - ${event.message}`}
        >
          <div className="alert-pulse" />
          <div className="alert-dot" />
        </div>
      );
    });

  /* ─────────────────────────── Dashboard ─────────────────────────── */
  const renderDashboard = () => {
    const general = Object.values(cieData.generals)[0] || {};
    const lastHb  = formatDateTime(cieData.lastHeartbeat) || "N/A";
    const total   = Object.keys(cieData.devices).length;
    const ok      = Object.values(cieData.devices).filter(d => d.status !== "FAULT").length;

    const counts = cieData.events.reduce((a, ev) => {
      a[ev.severity] = (a[ev.severity] || 0) + 1;
      return a;
    }, {});

    return (
      <div className="MonitoramentoLabel">
        <div className="dashboard-panel">
          {/* header */}
          <div className="dashboard-header">
            <h3>Status do Sistema</h3>
            <button className="dashboard-toggle" onClick={() => setShowDashboard(!showDashboard)}>
              {showDashboard ? "−" : "+"}
            </button>
          </div>

          {showDashboard && (
            <div className="dashboard-content">
              {/* SISTEMA */}
              <div className="dashboard-section">
                <h4>Sistema CIE</h4>
                <div className="dashboard-item"><span className="label">Versão CIE:</span><span className="value">{general.cieVersion || "N/A"}</span></div>
                <div className="dashboard-item"><span className="label">MAC Address:</span><span className="value">{general.macAddr || "N/A"}</span></div>
                <div className="dashboard-item"><span className="label">Último Heartbeat:</span><span className="value">{lastHb}</span></div>
              </div>

              {/* CONTADORES */}
              <div className="dashboard-section">
                <h4>Contadores</h4>
                <div className="dashboard-item"><span className="label">Alarmes:</span><span className={`value ${general.alarms > 0 ? "alert" : "normal"}`}>{general.alarms || 0}</span></div>
                <div className="dashboard-item"><span className="label">Supervisões:</span><span className={`value ${general.supervisions > 0 ? "warning" : "normal"}`}>{general.supervisions || 0}</span></div>
                <div className="dashboard-item"><span className="label">Falhas:</span><span className={`value ${general.failures > 0 ? "error" : "normal"}`}>{general.failures || 0}</span></div>
              </div>

              {/* DISPOSITIVOS */}
              <div className="dashboard-section">
                <h4>Dispositivos</h4>
                <div className="dashboard-item"><span className="label">Total:</span><span className="value">{total}</span></div>
                <div className="dashboard-item"><span className="label">Ativos:</span><span className="value normal">{ok}</span></div>
                <div className="dashboard-item"><span className="label">Com Falha:</span><span className="value error">{total - ok}</span></div>
              </div>

              {/* EVENTOS */}
              <div className="dashboard-section">
                <h4>Eventos (24h)</h4>
                <div className="dashboard-item"><span className="label">Incêndios:</span><span className="value alert">{counts.FIRE  || 0}</span></div>
                <div className="dashboard-item"><span className="label">Falhas:</span><span className="value warning">{counts.FAULT || 0}</span></div>
                <div className="dashboard-item"><span className="label">Informações:</span><span className="value normal">{counts.INFO  || 0}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ─────────────────────── Render map view ─────────────────────── */
  const renderMapView = () => (
    <div className="map-container">
      <div className="map-image-container">
        {/* Planta do edifício */}
        <div className="map-placeholder">
          <div className="building-layout">
            <img src={planta} alt="Planta do Edifício" className="building-img" />
          </div>

          {/* Legenda */}
          <div className="map-legend">
            <h4>Legenda:</h4>
            <div className="legend-item"><div className="legend-dot normal" /> <span>Normal</span></div>
            <div className="legend-item"><div className="legend-dot fault"  /> <span>Falha</span></div>
            <div className="legend-item"><div className="legend-dot fire"   /> <span>Incêndio</span></div>
          </div>
        </div>

        {/* Marcadores */}
        {renderDeviceMarkers()}
        {renderMapAlerts()}
      </div>

      {/* Popup de detalhes */}
      {selectedEvent && (
        <div className="event-details-popup">
          <div className="popup-header">
            <h4>Detalhes do Evento</h4>
            <button onClick={() => setSelectedEvent(null)}>×</button>
          </div>
          <div className="popup-content">
            <p><strong>Dispositivo:</strong> {selectedEvent.deviceName}</p>
            <p><strong>Zona:</strong> {selectedEvent.zoneName}</p>
            <p><strong>Tipo:</strong> {selectedEvent.raw?.DeviceType || "N/A"}</p>
            <p><strong>Severidade:</strong> <span className={`severity-badge ${selectedEvent.severity.toLowerCase()}`}>{selectedEvent.severity}</span></p>
            <p><strong>Mensagem:</strong> {selectedEvent.message}</p>
            <p><strong>Data/Hora:</strong> {formatDateTime(selectedEvent.ts)}</p>
            <p><strong>Sequência Global:</strong> {selectedEvent.globalSeq}</p>
            <p><strong>Loop:</strong> {selectedEvent.raw?.Loop || "N/A"}</p>
            <p><strong>Device ID:</strong> {selectedEvent.raw?.Device || "N/A"}</p>
          </div>
        </div>
      )}
    </div>
  );

  /* ─────────────────────── Render list view ─────────────────────── */
  const renderListView = () => {
    const filtered = severityFilter === "all"
      ? cieData.events
      : cieData.events.filter(e => e.severity.toLowerCase() === severityFilter);

    const recentEvents = filtered.slice(0, 100); // mostra no máximo 100

    return (
      <div className="events-list-container">
        {/* filtros */}
        <div className="list-controls">
          <div className="filter-group">
            <label>Filtrar por severidade:</label>
            <select
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
              className="severity-filter"
            >
              <option value="all">Todos</option>
              <option value="fire">Incêndio</option>
              <option value="fault">Falha</option>
              <option value="info">Informação</option>
            </select>
          </div>
          <div className="results-info">
            Mostrando {recentEvents.length} de {filtered.length} eventos
          </div>
        </div>

        {/* tabela */}
        <div className="events-table">
          <table>
            <thead>
              <tr>
                <th>Data/Hora</th><th>Severidade</th><th>Dispositivo</th>
                <th>Zona</th><th>Tipo</th><th>Mensagem</th>
                <th>Seq. Global</th><th>Loop</th>
              </tr>
            </thead>
            <tbody>
              {recentEvents.map((ev, i) => (
                <tr
                  key={`evt-${i}`}
                  className={`event-row ${ev.severity.toLowerCase()}`}
                  onClick={() => setSelectedEvent(ev)}
                >
                  <td>{formatDateTime(ev.ts)}</td>
                  <td>
                    <span className={`severity-badge ${ev.severity.toLowerCase()}`}>
                      {ev.severity}
                    </span>
                  </td>
                  <td>{ev.deviceName}</td>
                  <td>{ev.zoneName}</td>
                  <td>{ev.raw?.DeviceType || "N/A"}</td>
                  <td className="message-cell" title={ev.message}>{ev.message}</td>
                  <td>{ev.globalSeq}</td>
                  <td>{ev.raw?.Loop || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {recentEvents.length === 0 && (
            <div className="no-events">
              <p>Nenhum evento encontrado com os filtros aplicados</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ──────────────────────────── Loader ──────────────────────────── */
  if (loading) {
    return (
      <div className="MonitoramentoContent">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Carregando dados de monitoramento…</p>
        </div>
      </div>
    );
  }

  /* ──────────────────────────── Render ──────────────────────────── */
  return (
    <div className="MonitoramentoContent">
      {/* Header */}
      <div className="monitoring-header">
        <h1>Monitoramento de CIE</h1>

        <div className="controls-panel">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === "mapa" ? "active" : ""}`}
              onClick={() => setViewMode("mapa")}
            >
              🗺️ Mapa
            </button>
            <button
              className={`toggle-btn ${viewMode === "lista" ? "active" : ""}`}
              onClick={() => setViewMode("lista")}
            >
              📋 Lista
            </button>
          </div>
          <button
            className="refresh-btn"
            onClick={() => fetchCieData(true)} // força loading no refresh manual
            disabled={loading}
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Corpo */}
      <div className="monitoring-body">
        <div className="main-content">
          {viewMode === "mapa" ? renderMapView() : renderListView()}
        </div>
        <div className="sidebar">{renderDashboard()}</div>
      </div>
    </div>
  );
}